import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useAllMerchant } from '../../Query/MerchantQuery';
import {
  UseCreateChoiceGroup,
  useEditChoiceGroup,
} from '../../Query/ChoiceGroupQuery';
import { UseCreateChoice } from '../../Query/ChoiceQuery';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { useAtom } from 'jotai';
import { choiceGroupAtom } from './ManageChoiceGroupTable';
import { useEffect, useMemo, useState } from 'react';
import { useManageCmsParams } from '../ManageCMS/useManageCmsParams';
import React from 'react';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useChoiceList } from '../../Query/ChoiceListQuery';
import { FieldProps, FormikProps } from 'formik';
import { alpha } from '@mui/material/styles';
import { COLORS } from '../../../theme/themeConstants';
import { useQueryClient } from '@tanstack/react-query';

interface MerchantData {
  id: string;
  name: string;
}

// Validation schema depends on add/edit mode
const validationSchema = (choiceGroupDataId: any) =>
  Yup.object().shape({
    choiceGroupNameEnglish: Yup.string()
      .required('Please enter Choice Group Name')
      .matches(
        /^[A-Za-z][A-Za-z\s]*$/,
        'Choice Group Name should only contain letters and spaces, and not start with a space'
      )
      .max(100, 'Choice Group Name cannot exceed 100 characters')
      .test(
        'not-only-spaces',
        'Choice Group Name cannot be only spaces',
        (value) => typeof value === 'string' && value.trim().length > 0
      ),

    choiceGroupNameArabic: Yup.string()
      .required('Please enter Choice Group Name in Arabic')
      .matches(
        /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
        'Name should only contain Arabic letters and spaces, and not start with a space'
      )
      .max(50, 'Choice Group Name cannot exceed 50 characters')
      .test(
        'not-only-spaces',
        'Name cannot be only spaces',
        (value) => typeof value === 'string' && value.trim().length > 0
      ),
    minimumLimit: Yup.string()
      .required('0 to 100')
      .test(
        'required-minimum-limit',
        'Minimum limit must be 1 when Choice Group Option is Required',
        function (value) {
          const { choiceGroupOption } = this.parent;
          if (choiceGroupOption === 'Required') {
            const numValue = Number(value);
            return !isNaN(numValue) && numValue === 1;
          }
          return true;
        }
      )
      .test(
        'optional-minimum-limit',
        'Minimum limit must be 0 when Choice Group Option is Optional',
        function (value) {
          const { choiceGroupOption } = this.parent;
          if (choiceGroupOption === 'Optional') {
            const numValue = Number(value);
            return !isNaN(numValue) && numValue === 0;
          }
          return true;
        }
      )
      .test(
        'not-exceed-maximum',
        'Minimum limit must not be more than Maximum limit',
        function (value) {
          const { maximumLimit } = this.parent;
          if (value && maximumLimit) {
            const minValue = Number(value);
            const maxValue = Number(maximumLimit);
            return !isNaN(minValue) && !isNaN(maxValue) && minValue <= maxValue;
          }
          return true;
        }
      ),
    maximumLimit: Yup.string()
      .required('0 to 100')
      .test(
        'must-be-greater-equal-minimum',
        'Maximum limit must be greater than or equal to Minimum limit',
        function (value) {
          const { minimumLimit } = this.parent;
          if (value && minimumLimit) {
            const minValue = Number(minimumLimit);
            const maxValue = Number(value);
            return !isNaN(minValue) && !isNaN(maxValue) && maxValue >= minValue;
          }
          return true;
        }
      )
      .test(
        'not-exceed-choices-length',
        'Maximum limit cannot exceed the number of selected choices',
        function (value) {
          const { choiceDetails } = this.parent;
          if (value && choiceDetails && Array.isArray(choiceDetails)) {
            const numValue = Number(value);
            return !isNaN(numValue) && numValue <= choiceDetails.length;
          }
          return true;
        }
      ),
    merchantId: choiceGroupDataId 
      ? Yup.string() 
      : Yup.string().when('storeType', {
          is: (storeType: string) => storeType && storeType.length > 0,
          then: (schema) => schema.required('Please select a merchant'),
          otherwise: (schema) => schema.optional().nullable(),
        }),
    storeType: choiceGroupDataId 
      ? Yup.string() 
      : Yup.string().required('Please select Store Type'),
    storeId: choiceGroupDataId
      ? Yup.string()
      : Yup.string().when(['merchantId', 'storeType'], {
        is: (merchantId: string, storeType: string) => 
          merchantId && merchantId.length > 0 && storeType && storeType.length > 0,
        then: (schema) => schema.required('Please select Store'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    choiceDetails: Yup.array()
      .of(Yup.string())
      .test(
        'no-choices-available',
        'Please select a different store to add choice',
        function (value) {
          const { noChoicesAvailable } = this.options.context || {};
          if (noChoicesAvailable) {
            return false;
          }
          return value && value.length > 0;
        }
      )
      .when(['storeId', 'storeType'], {
        is: (storeId: string, storeType: string) => {
          const shouldValidate = storeId && storeId.length > 0 && storeType && storeType.length > 0;
          return shouldValidate;
        },
        then: (schema) => schema.required('Please select at least one choice'),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    choiceGroupOption: Yup.string().required('Please select choiceGroupOption'),
  });

const AddChoiceGroup = ({ onCancel }: { onCancel: () => void }) => {
  const [open, setOpen] = React.useState(false);
  const [choiceGroupDataId] = useAtom(choiceGroupAtom);
  const { warning } = useThemeColors();
  const queryClient = useQueryClient();
  const { mutate: CreateChoiceGroup, isPending: CreateChoiceGrouLoading } =
    UseCreateChoiceGroup();
  const { mutate: EditChoiceGroup, isPending: EditChoiceGroupLoading } =
    useEditChoiceGroup();
  const { mutate: CreateChoice, isPending: CreateChoiceLoading } = UseCreateChoice();

  // State for Add Choice dialog
  const [addChoiceDialogOpen, setAddChoiceDialogOpen] = useState(false);
  const [newChoiceData, setNewChoiceData] = useState({
    choiceNameEnglish: '',
    choiceNameArabic: '',
    price: '',
  });
  const [addChoiceErrors, setAddChoiceErrors] = useState({
    choiceNameEnglish: '',
    choiceNameArabic: '',
    price: '',
  });

  const { params, dispatch } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  // const { data: choiceData } = useAllChoices(memoizedParams);
  const [selectedStoreId, setSelectedStoreId] = useState(choiceGroupDataId?.storeId ?? '');
  const { data: choiceListData } = useChoiceList(selectedStoreId ? { storeId: selectedStoreId } : {});

  const [selectedStoreType, setSelectedStoreType] = useState(choiceGroupDataId ? choiceGroupDataId?.storeType : '');
  const [isMerchantLoading, setIsMerchantLoading] = useState(false);

  const { data: merchantData, refetch } = useAllMerchant({
    initialData: {
      storeType: selectedStoreType || undefined,
    },
  });

  const [options, setMerchantOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [selectedMerchantId, setSelectedMerchantId] = useState(choiceGroupDataId?.merchantDetails?.merchantId ?? '');
  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string }[]>([]);

  const {
    data: storesData, isLoading: isStoresLoading
  } = useAllStores(memoizedParams);

  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.nameEnglish,
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  useEffect(() => {
    if (merchantData) {
      const merchantOptions = merchantData.map((item: MerchantData) => ({
        value: item.id,
        label: item.name,
      }));
      setMerchantOptions(merchantOptions);
    }
  }, [merchantData]);

  const choiceDataOption = choiceListData?.map((item: any) => ({
    value: item.choiceId,
    label: item.choiceName,
  }));

  // Determine if there are no choices available for the selected store
  const noChoicesAvailable = !choiceDataOption || choiceDataOption.length === 0;

  // Clear choiceDetails error when store changes and no choices are available
  useEffect(() => {
    if (selectedStoreId && noChoicesAvailable) {
      // This will be handled by the validation context
    }
  }, [selectedStoreId, noChoicesAvailable]);

  const refetchMerchants = async () => {
    setIsMerchantLoading(true);
    try {
      await refetch();
    } finally {
      setIsMerchantLoading(false);
    }
  };

  // Custom reset function that respects dependencies
  const handleReset = (formikProps: any) => {
    // Clear all form fields explicitly
    formikProps.setFieldValue('choiceGroupNameEnglish', '');
    formikProps.setFieldValue('choiceGroupNameArabic', '');
    formikProps.setFieldValue('minimumLimit', '');
    formikProps.setFieldValue('maximumLimit', '');
    formikProps.setFieldValue('merchantId', '');
    formikProps.setFieldValue('storeId', '');
    formikProps.setFieldValue('choiceDetails', []);
    formikProps.setFieldValue('choiceGroupOption', '');
    formikProps.setFieldValue('storeType', '');
    
    // Clear all field errors
    formikProps.setFieldError('choiceGroupNameEnglish', undefined);
    formikProps.setFieldError('choiceGroupNameArabic', undefined);
    formikProps.setFieldError('minimumLimit', undefined);
    formikProps.setFieldError('maximumLimit', undefined);
    formikProps.setFieldError('merchantId', undefined);
    formikProps.setFieldError('storeId', undefined);
    formikProps.setFieldError('choiceDetails', undefined);
    formikProps.setFieldError('choiceGroupOption', undefined);
    formikProps.setFieldError('storeType', undefined);
    
    // Reset touched state
    formikProps.setFieldTouched('choiceGroupNameEnglish', false);
    formikProps.setFieldTouched('choiceGroupNameArabic', false);
    formikProps.setFieldTouched('minimumLimit', false);
    formikProps.setFieldTouched('maximumLimit', false);
    formikProps.setFieldTouched('merchantId', false);
    formikProps.setFieldTouched('storeId', false);
    formikProps.setFieldTouched('choiceDetails', false);
    formikProps.setFieldTouched('choiceGroupOption', false);
    formikProps.setFieldTouched('storeType', false);
    
    // Reset form using resetForm
    formikProps.resetForm({
      values: {
        choiceGroupNameEnglish: '',
        choiceGroupNameArabic: '',
        minimumLimit: '',
        maximumLimit: '',
        merchantId: '',
        storeId: '',
        choiceDetails: [],
        choiceGroupOption: '',
        storeType: '',
      },
    });
    
    setSelectedStoreType('');
    setSelectedMerchantId('');
    setSelectedStoreId('');
    setMerchantOptions([]);
    setStoreOptions([]);
  };

  // Handle Add Choice dialog
  const handleAddChoiceClick = () => {
    if (!selectedStoreId || !selectedMerchantId || !selectedStoreType) {
      return;
    }
    setAddChoiceDialogOpen(true);
  };

  const validateAddChoiceForm = () => {
    const errors = {
      choiceNameEnglish: '',
      choiceNameArabic: '',
      price: '',
    };

    // Validate English name
    if (!newChoiceData.choiceNameEnglish || newChoiceData.choiceNameEnglish.trim() === '') {
      errors.choiceNameEnglish = 'Choice Name (English) is required';
    } else if (!/^[A-Za-z][A-Za-z\s]*$/.test(newChoiceData.choiceNameEnglish.trim())) {
      errors.choiceNameEnglish = 'Choice Name should only contain letters and spaces, and not start with a space';
    } else if (newChoiceData.choiceNameEnglish.trim().length > 100) {
      errors.choiceNameEnglish = 'Choice Name cannot exceed 100 characters';
    }

    // Validate Arabic name
    if (!newChoiceData.choiceNameArabic || newChoiceData.choiceNameArabic.trim() === '') {
      errors.choiceNameArabic = 'Choice Name (Arabic) is required';
    } else if (!/^[\u0600-\u06FF][\u0600-\u06FF\s]*$/.test(newChoiceData.choiceNameArabic.trim())) {
      errors.choiceNameArabic = 'Name should only contain Arabic letters and spaces, and not start with a space';
    } else if (newChoiceData.choiceNameArabic.trim().length > 50) {
      errors.choiceNameArabic = 'Choice Name cannot exceed 50 characters';
    }

    // Validate price
    if (!newChoiceData.price || newChoiceData.price.trim() === '') {
      errors.price = 'Price is required';
    } else {
      const priceNum = parseFloat(newChoiceData.price);
      if (isNaN(priceNum)) {
        errors.price = 'Price must be a valid number';
      } else if (priceNum <= 0 || priceNum === 0) {
        errors.price = 'Price must be greater than 0';
      }
    }

    setAddChoiceErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleAddChoiceSubmit = () => {
    // Validate before submitting
    const isValid = validateAddChoiceForm();
    if (!isValid) {
      // Show validation errors
      return;
    }

    const choiceData = {
      choiceNameEnglish: newChoiceData.choiceNameEnglish.trim(),
      choiceNameArabic: newChoiceData.choiceNameArabic.trim(),
      price: +newChoiceData.price,
      storeType: selectedStoreType,
      storeId: selectedStoreId,
      merchantId: selectedMerchantId,
      addedBy: 'Admin',
    };

    CreateChoice(
      { modifiedValues: choiceData, storeId: selectedStoreId },
      {
        onSuccess: () => {
          setAddChoiceDialogOpen(false);
          setNewChoiceData({
            choiceNameEnglish: '',
            choiceNameArabic: '',
            price: '',
          });
          setAddChoiceErrors({
            choiceNameEnglish: '',
            choiceNameArabic: '',
            price: '',
          });
          // Invalidate choice-related queries to refresh the data
          queryClient.invalidateQueries({
            queryKey: ['getChoiceGroup'],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ['getChoices'],
            exact: false,
          });
        },
        onError: (error: any) => {
          console.log('Error creating choice:', error);
        },
      }
    );
  };

  const handleAddChoiceCancel = () => {
    setAddChoiceDialogOpen(false);
    setNewChoiceData({
      choiceNameEnglish: '',
      choiceNameArabic: '',
      price: '',
    });
    setAddChoiceErrors({
      choiceNameEnglish: '',
      choiceNameArabic: '',
      price: '',
    });
  };

  return (
    <>
      {AllForm({
        title: 'Add SubAdmin',
        initialValues: {
          choiceGroupNameEnglish:
            choiceGroupDataId?.choiceGroupNameEnglish ?? '',
          choiceGroupNameArabic: choiceGroupDataId?.choiceGroupNameArabic ?? '',
          minimumLimit: choiceGroupDataId?.minimumLimit ?? null,
          maximumLimit: choiceGroupDataId?.maximumLimit ?? null,
          merchantId: choiceGroupDataId?.merchantDetails?.merchantId ?? '',
          storeId: choiceGroupDataId?.storeId ?? '',
          choiceDetails: choiceGroupDataId?.choiceDetails ?? [],
          choiceGroupOption:
            choiceGroupDataId?.choiceGroupOption === true
              ? 'Required'
              : choiceGroupDataId?.choiceGroupOption === false
                ? 'Optional'
                : '',
          storeType: choiceGroupDataId?.storeType ?? ''
        },
        validationSchema: useMemo(() => validationSchema(choiceGroupDataId), [choiceGroupDataId, noChoicesAvailable, selectedStoreId]),
        validationContext: { 
          noChoicesAvailable: noChoicesAvailable && selectedStoreId 
        },
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: async (values: any) => {
          // For edit, use values from choiceGroupDataId; for add, use form values
          const modifiedValues = choiceGroupDataId
            ? {
              choiceGroupNameEnglish: values.choiceGroupNameEnglish,
              choiceGroupNameArabic: values.choiceGroupNameArabic,
              minimumLimit: +values.minimumLimit,
              maximumLimit: +values.maximumLimit,
              choiceGroupOption: values.choiceGroupOption === "Required",
              choiceDetails: values.choiceDetails,
              merchantList: [choiceGroupDataId.merchantDetails?.merchantId],
              storeId: choiceGroupDataId.storeId,
              storeType: choiceGroupDataId.storeType,
              addedBy: "Admin"
            }
            : {
              choiceGroupNameEnglish: values.choiceGroupNameEnglish,
              choiceGroupNameArabic: values.choiceGroupNameArabic,
              minimumLimit: +values.minimumLimit,
              maximumLimit: +values.maximumLimit,
              choiceGroupOption: values.choiceGroupOption === "Required",
              choiceDetails: values.choiceDetails,
              merchantList: [values.merchantId],
              storeId: values.storeId,
              storeType: values.storeType,
              addedBy: "Admin"
            };
          try {
            if (choiceGroupDataId) {
              EditChoiceGroup(
                {
                  id: choiceGroupDataId.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                    // closeForm();
                    // resetForm();
                  },
                  onError: (error: any) => {
                    console.log(error);
                    // setErrors(parseJavaError(error));
                  },
                }
              );
            } else {
              CreateChoiceGroup(
                { data: modifiedValues, storeId: values.storeId },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.log(error);
                  },
                }
              );
            }
          } catch (error) {
            console.log(error);
            // setErrors(parseJavaError(error));
          }
        },

        fields: [
          {
            name: 'choiceGroupNameEnglish',
            label: 'Choice Group Name:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'choiceGroupNameArabic',
            label: 'Choice Group Name (In Arabic):',
            type: 'text',
            isRequired: true,
          },
          ...(!choiceGroupDataId ? [
            {
              name: 'storeType',
              label: 'Select Store Type:',
              type: 'customWithError',
              isRequired: true,
              disabled: false,
              component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => (
                <FormControl fullWidth>
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    displayEmpty
                    sx={{ minWidth: 'unset', width: 400 }}
                    onChange={async (e) => {
                      const value = e.target.value as string;
                      
                      // Clear errors immediately when value is selected
                      if (value) {
                        form.setFieldError('storeType', undefined);
                        form.setFieldError('merchantId', undefined);
                        form.setFieldError('storeId', undefined);
                        form.setFieldError('choiceDetails', undefined);
                      }
                      
                      form.setFieldValue('storeType', value);
                      form.setFieldTouched('storeType', true, false);
                      
                      setSelectedStoreType(value);
                      // Reset dependent fields when store type changes
                      form.setFieldValue('merchantId', '');
                      form.setFieldValue('storeId', '');
                      form.setFieldTouched('merchantId', false);
                      form.setFieldTouched('storeId', false);
                      form.setFieldError('merchantId', undefined);
                      form.setFieldError('storeId', undefined);
                      setSelectedMerchantId('');
                      setSelectedStoreId('');
                      setStoreOptions([]);
                      
                      // Mark field as touched and validate after a small delay
                      setTimeout(() => {
                        form.setFieldTouched('storeType', true, false);
                        if (value) {
                          form.validateField('storeType');
                        }
                      }, 100);
                      
                      await refetchMerchants();
                    }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                        -- Select --
                      </span>
                    </MenuItem>
                    {[
                      { label: 'Restaurant', value: 'Restaurant' },
                      { label: 'Grocery Store', value: 'GroceryStore' },
                      { label: 'Pharmacy Store', value: 'PharmacyStore' },
                    ].map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),
            },
            {
              name: 'merchantId',
              label: 'Select Merchant:',
              type: 'customWithError',
              isRequired: true,
              disabled: !selectedStoreType || isMerchantLoading,
              component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                const isDisabled = !selectedStoreType || isMerchantLoading;
                return (
                  <FormControl fullWidth>
                    <Autocomplete
                      options={options}
                      getOptionLabel={(option) => option.label || ''}
                      value={options.find((opt) => opt.value === field.value) || null}
                      onChange={(_, newValue) => {
                        const value = newValue ? newValue.value : '';
                        
                        // Clear errors immediately when value is selected
                        if (value) {
                          form.setFieldError('merchantId', undefined);
                          form.setFieldError('storeId', undefined);
                          form.setFieldError('choiceDetails', undefined);
                        }
                        
                        form.setFieldValue('merchantId', value);
                        form.setFieldTouched('merchantId', true, false);
                        
                        setSelectedMerchantId(value);
                        // Reset store selection when merchant changes
                        form.setFieldValue('storeId', '');
                        form.setFieldTouched('storeId', false);
                        form.setFieldError('storeId', undefined);
                        setSelectedStoreId('');
                        dispatch({ type: 'SET_MERCHANT_ID', payload: value as string });
                        
                        // Mark field as touched and validate after a small delay
                        setTimeout(() => {
                          form.setFieldTouched('merchantId', true, false);
                          if (value) {
                            form.validateField('merchantId');
                          }
                        }, 100);
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          placeholder={
                            !selectedStoreType
                              ? '-- Select Store First --'
                              : isMerchantLoading
                                ? '-- Loading Merchants... --'
                                : '-- Select Merchant --'
                          }
                          sx={{
                            filter: isDisabled ? 'blur(1px)' : 'none',
                            backgroundColor: isDisabled ? '#f5f5f5' : 'inherit',
                            minWidth: 'unset',
                            width: 400,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                );
              },
            },
            {
              name: 'storeId',
              label: 'Select Store:',
              type: 'customWithError',
              isRequired: true,
              disabled: !selectedMerchantId || isStoresLoading,
              component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                const isDisabled = !form.values.merchantId || isStoresLoading;
                const noStoresAvailable = !isStoresLoading && form.values.merchantId && storeOptions.length === 0;
                const shouldDisable = isDisabled || noStoresAvailable;
                return (
                  <FormControl fullWidth>
                    <Autocomplete
                      options={storeOptions}
                      getOptionLabel={(option) => option.label || ''}
                      value={storeOptions.find((opt) => opt.value === field.value) || null}
                      onChange={(_, newValue) => {
                        const value = newValue ? newValue.value : '';
                        // Clear errors immediately when value is selected
                        if (value) {
                          form.setFieldError('storeId', undefined);
                          form.setFieldError('choiceDetails', undefined);
                        }
                        form.setFieldValue('storeId', value);
                        setSelectedStoreId(value as string);
                        dispatch({ type: 'SET_MERCHANT_ID', payload: selectedMerchantId as string });
                        // Mark field as touched and validate after a small delay
                        setTimeout(() => {
                          form.setFieldTouched('storeId', true, false);
                          form.validateField('storeId');
                        }, 100);
                      }}
                      disabled={shouldDisable}
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          placeholder={
                            !form.values.merchantId
                              ? '-- Select Merchant First --'
                              : isStoresLoading
                                ? '-- Loading Stores... --'
                                : noStoresAvailable
                                  ? 'No store available'
                                  : '-- Select Store --'
                          }
                          sx={{
                            filter: shouldDisable ? 'blur(1px)' : 'none',
                            backgroundColor: shouldDisable ? '#f5f5f5' : 'inherit',
                            minWidth: 'unset',
                            width: 400,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                );
              },
            },
          ] : []),
          {
            name: 'choiceDetails',
            label: 'Add Choice:',
            type: 'customWithError',
            isRequired: true,
            disabled: !selectedStoreId || noChoicesAvailable,
            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
              const selectedCategoryIds: string[] = Array.isArray(field.value)
                ? field.value
                : [];
              const isChoiceSelectDisabled = !selectedStoreId || noChoicesAvailable;

              const handleSelectChange = (
                event: SelectChangeEvent<string[]>
              ) => {
                const { value } = event.target;
                const newValue =
                  typeof value === 'string' ? value.split(',') : value;

                // Clear error immediately when choices are selected
                if (newValue && newValue.length > 0) {
                  form.setFieldError(field.name, undefined);
                }

                // Set value and trigger validation
                form.setFieldValue(field.name, newValue, true);
                // Mark field as touched and validate after a small delay
                setTimeout(() => {
                  form.setFieldTouched(field.name, true, false);
                  form.validateField(field.name);
                  // Also validate maximumLimit since it depends on choiceDetails length
                  form.validateField('maximumLimit');
                }, 100);
                // Close dropdown after selection
                setOpen(false);
              };

              const handleChipDelete = (categoryIdToDelete: string) => () => {
                const newSelectedCategoryIds = selectedCategoryIds.filter(
                  (categoryId) => categoryId !== categoryIdToDelete
                );
                // Clear error if no choices are selected
                if (newSelectedCategoryIds.length === 0) {
                  form.setFieldError(field.name, undefined);
                }
                form.setFieldValue(field.name, newSelectedCategoryIds, true);
                // Mark field as touched and validate after a small delay
                setTimeout(() => {
                  form.setFieldTouched(field.name, true, false);
                  form.validateField(field.name);
                  // Also validate maximumLimit since it depends on choiceDetails length
                  form.validateField('maximumLimit');
                }, 100);
              };

              const selectedCategories = (choiceDataOption || []).filter(
                (option: any) => selectedCategoryIds.includes(option.value)
              );

              const shouldBlur = isChoiceSelectDisabled;
            
              return (
                <FormControl fullWidth sx={{ width: '401px' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Select Dropdown and Add Choice Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        multiple
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        value={selectedCategoryIds}
                        onChange={handleSelectChange}
                        input={<OutlinedInput />}
                        displayEmpty
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return (
                              <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
                                {!selectedStoreId
                                  ? 'Select Store First'
                                  : noChoicesAvailable
                                    ? 'Please select a different store'
                                    : 'Select Category'}
                              </span>
                            );
                          }
                          return '';
                        }}
                        sx={{
                          width: '320px',
                          filter: shouldBlur ? 'blur(1px)' : 'none',
                          backgroundColor: shouldBlur ? '#f5f5f5' : 'inherit',
                          minWidth: 'unset',
                        }}
                        disabled={isChoiceSelectDisabled}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                            {!selectedStoreId
                              ? '-- Select Store First --'
                              : noChoicesAvailable
                                ? 'Please select a different store'
                                : '-- Select Choice --'}
                          </span>
                        </MenuItem>
                        {!noChoicesAvailable && selectedStoreId && (choiceDataOption || []).map((option: any) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      
                      {/* Add Choice Button */}
                      <Tooltip title={!selectedStoreId ? 'Select store first' : 'Add a new choice'} arrow>
                        <span>
                          <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
                            onClick={handleAddChoiceClick}
                            disabled={!selectedStoreId || !selectedMerchantId || !selectedStoreType}
                            sx={{
                              minWidth: '130px',
                              height: '40px',
                              px: 1.75,
                              fontSize: 13,
                              lineHeight: 1.2,
                              borderRadius: 1.5,
                              textTransform: 'none',
                              borderColor: COLORS.primary.main,
                              color: COLORS.primary.main,
                              backgroundColor: alpha(COLORS.primary.main, 0.02),
                              '&:hover': {
                                borderColor: alpha(COLORS.primary.main, 0.9),
                                backgroundColor: alpha(COLORS.primary.main, 0.06),
                              },
                              '&:disabled': {
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'rgba(0, 0, 0, 0.26)',
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            Add Choice
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>

                    {/* Chips shown in a new row below the select */}
                    {selectedCategories.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {selectedCategories.map((category: any) => (
                          <Chip
                            key={category.value}
                            label={category.label}
                            onDelete={handleChipDelete(category.value)}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </FormControl>
              );
            },
          },
          {
            name: 'choiceGroupOption',
            label: 'Choice Group Option:',
            type: 'radio',
            isRequired: true,
            radioOptions: ['Required', 'Optional'],
          },
          {
            name: 'minimumLimit',
            label: 'Minimum Limit:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'maximumLimit',
            label: 'Maximum Limit:',
            type: 'text',
            isRequired: true,
          },
       
       
        ],

        // customButton: (
        //   <Button
        //     type="submit"
        //     variant="contained"
        //     sx={{
        //       textTransform: 'none',
        //       fontSize: 14,
        //       px: 3,
        //       py: 1,
        //       backgroundColor: 'rgb(76, 10, 39)',
        //       '&:hover': {
        //         backgroundColor: 'rgb(76, 10, 39, 0.8)',
        //       },
        //       '&:disabled': {
        //         backgroundColor: 'rgba(0, 0, 0, 0.12)',
        //         color: 'rgba(0, 0, 0, 0.26)',
        //       },
        //     }}
        //   >
        //     Create
        //   </Button>
        // ),
        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': { backgroundColor: COLORS.primary.dark },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={async (e) => {
                e.preventDefault();
                // Touch all fields to show validation errors
                const fieldNames = ['choiceGroupNameEnglish', 'choiceGroupNameArabic', 'minimumLimit', 'maximumLimit', 'choiceGroupOption'];
                if (!choiceGroupDataId) {
                  fieldNames.push('storeType', 'merchantId', 'storeId', 'choiceDetails');
                }
                fieldNames.forEach(fieldName => {
                  if (typeof formikProps.setFieldTouched === 'function') {
                    formikProps.setFieldTouched(fieldName, true, false);
                  }
                });
                // Validate the form to ensure all errors are populated
                const errors = await formikProps.validateForm();

                if (Object.keys(errors).length === 0) {
                  formikProps.handleSubmit();
                } else {
                  console.log('Validation failed:', errors);
                }
              }}
              disabled={CreateChoiceGrouLoading || EditChoiceGroupLoading}
            >
              {CreateChoiceGrouLoading || EditChoiceGroupLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {choiceGroupDataId ? 'Updating...' : 'Creating...'}
                </>
              ) : choiceGroupDataId ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>

            {!choiceGroupDataId && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => handleReset(formikProps)}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderColor: COLORS.secondary.main,
                  color: COLORS.secondary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                Reset
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        ),
      })()}
      
      {/* Add Choice Dialog */}
      <Dialog 
        open={addChoiceDialogOpen} 
        onClose={handleAddChoiceCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Choice</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField
              label="Choice Name (English)"
              value={newChoiceData.choiceNameEnglish}
              onChange={(e) => {
                setNewChoiceData(prev => ({ ...prev, choiceNameEnglish: e.target.value }));
                if (addChoiceErrors.choiceNameEnglish) {
                  setAddChoiceErrors(prev => ({ ...prev, choiceNameEnglish: '' }));
                }
              }}
              onBlur={() => {
                if (newChoiceData.choiceNameEnglish) {
                  validateAddChoiceForm();
                }
              }}
              fullWidth
              required
              error={Boolean(addChoiceErrors.choiceNameEnglish)}
              helperText={addChoiceErrors.choiceNameEnglish}
              placeholder="Enter choice name in English"
              sx={{ mb: 0.5 }}
            />
            <TextField
              label="Choice Name (Arabic)"
              value={newChoiceData.choiceNameArabic}
              onChange={(e) => {
                setNewChoiceData(prev => ({ ...prev, choiceNameArabic: e.target.value }));
                if (addChoiceErrors.choiceNameArabic) {
                  setAddChoiceErrors(prev => ({ ...prev, choiceNameArabic: '' }));
                }
              }}
              onBlur={() => {
                if (newChoiceData.choiceNameArabic) {
                  validateAddChoiceForm();
                }
              }}
              fullWidth
              required
              error={Boolean(addChoiceErrors.choiceNameArabic)}
              helperText={addChoiceErrors.choiceNameArabic}
              placeholder="Enter choice name in Arabic"
              sx={{ mb: 0.5 }}
            />
            <TextField
              label="Price (JOD)"
              type="number"
              value={newChoiceData.price}
              onChange={(e) => {
                const value = e.target.value;
                // Prevent "0" from being entered
                if (value === '0' || value === '0.') {
                  setAddChoiceErrors(prev => ({ ...prev, price: 'Price must be greater than 0' }));
                  return;
                }
                setNewChoiceData(prev => ({ ...prev, price: value }));
                // Clear error if value is valid
                if (addChoiceErrors.price && value) {
                  const priceNum = parseFloat(value);
                  if (!isNaN(priceNum) && priceNum > 0) {
                    setAddChoiceErrors(prev => ({ ...prev, price: '' }));
                  }
                }
                // Validate on change
                if (value) {
                  setTimeout(() => validateAddChoiceForm(), 100);
                }
              }}
              onBlur={() => {
                if (newChoiceData.price) {
                  validateAddChoiceForm();
                }
              }}
              fullWidth
              required
              error={Boolean(addChoiceErrors.price)}
              helperText={addChoiceErrors.price}
              placeholder="Enter price"
              inputProps={{ min: 0.01, step: 0.01 }}
              sx={{ mb: 0.5 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddChoiceCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleAddChoiceSubmit}
            variant="contained"
            disabled={CreateChoiceLoading}
            sx={{
              backgroundColor: COLORS.primary.main,
              '&:hover': {
                backgroundColor: alpha(COLORS.primary.main, 0.8),
              },
            }}
          >
            {CreateChoiceLoading ? (
              <>
                <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Choice'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddChoiceGroup;  
